import { z } from 'zod'

/**
 * Per-gym grade configuration — the admin-owned definition of a gym's grade
 * scale, per discipline. A route's `grade` column stores a *rank* (1..N) that
 * indexes into the discipline's ordered grade list here; the label, colour,
 * difficulty calibration, and points all live in this config, not on the route.
 *
 * This is the single source of truth that replaces the old hard-coded 1..8.
 * Every gym gets DEFAULT_GRADE_CONFIG on creation, which reproduces the legacy
 * eight numeric bands exactly, so existing gyms are unchanged.
 */

export const DISCIPLINES = ['boulder', 'lead'] as const
export type Discipline = (typeof DISCIPLINES)[number]

/**
 * Which disciplines a gym has (`gyms.disciplines`), picked by a super admin.
 * The gym and member apps show only the ones it names.
 */
export const GYM_DISCIPLINE_MODES = ['both', 'boulder', 'lead'] as const
export type GymDisciplineMode = (typeof GYM_DISCIPLINE_MODES)[number]

/**
 * The disciplines a gym shows, in display order. Anything that is not a known
 * single discipline reads as both, so a payload cached before the field existed
 * changes nothing.
 */
export function enabledDisciplines(mode: string | null | undefined): Discipline[] {
  return mode === 'boulder' || mode === 'lead' ? [mode] : [...DISCIPLINES]
}

/** The discipline a gym lands on when nothing more specific is asked for. */
export function defaultDiscipline(mode: string | null | undefined): Discipline {
  return enabledDisciplines(mode)[0]
}

/** Hard upper bound on how many grades a single discipline may define. */
export const MAX_GRADES_PER_DISCIPLINE = 40

/**
 * The canonical reference scale. Every gym grade is calibrated against French
 * grades (V-scale converts cleanly from French, so one reference covers both
 * disciplines for now). Ordered easiest → hardest; the array index is the
 * difficulty value used for cross-gym normalisation later.
 */
export const FRENCH_SCALE = [
  '4',
  '5a',
  '5b',
  '5c',
  '6a',
  '6a+',
  '6b',
  '6b+',
  '6c',
  '6c+',
  '7a',
  '7a+',
  '7b',
  '7b+',
  '7c',
  '7c+',
  '8a',
  '8a+',
  '8b',
  '8b+',
  '8c',
  '8c+',
  '9a',
  '9a+',
  '9b',
  '9b+',
  '9c',
] as const
export type FrenchGrade = (typeof FRENCH_SCALE)[number]

/** Difficulty index of a French grade on the reference scale, or -1 if unknown. */
export function frenchDifficulty(grade: string): number {
  return (FRENCH_SCALE as readonly string[]).indexOf(grade)
}

export interface GradeDef {
  /** Position in the discipline's ordered scale, 1..N, dense, easy → hard. */
  rank: number
  /** What climbers see: "1", "RED", "7A+", "28". */
  label: string
  /** Display colour as a hex string, or null. */
  color: string | null
  /** Calibration interval, lower bound, as a French grade (null = uncalibrated). */
  frenchMin: string | null
  /** Calibration interval, upper bound (null = open-ended "above frenchMin"). */
  frenchMax: string | null
  /** Points awarded for topping a route of this grade. */
  baseScore: number
}

export interface DisciplineGradeConfig {
  grades: GradeDef[]
}

/**
 * Only bouldering is configurable. Lead is always the fixed French scale
 * (LEAD_GRADES); a legacy `lead` key may still sit on older gym rows and is
 * ignored everywhere.
 */
export interface GymGradeConfig {
  boulder: DisciplineGradeConfig
  lead?: DisciplineGradeConfig
}

const gradeDefSchema = z.object({
  rank: z.number().int().min(1),
  label: z.string().min(1).max(20),
  color: z.string().max(32).nullable(),
  frenchMin: z.string().max(8).nullable(),
  frenchMax: z.string().max(8).nullable(),
  baseScore: z.number().int().min(0),
})

const disciplineConfigSchema = z.object({
  grades: z
    .array(gradeDefSchema)
    .min(1, 'A discipline needs at least one grade')
    .max(MAX_GRADES_PER_DISCIPLINE, 'Too many grades')
    .superRefine((grades, ctx) => {
      // Ranks must be a dense 1..N sequence so charts and filters can assume a
      // contiguous domain (the old zero-fill logic relied on this).
      const ranks = [...grades].map((g) => g.rank).sort((a, b) => a - b)
      ranks.forEach((rank, i) => {
        if (rank !== i + 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Grade ranks must be a contiguous 1..N sequence (got a gap or duplicate at ${rank})`,
          })
        }
      })
    }),
})

// Only boulder is configurable. A `lead` key sent by an older client is
// stripped (zod drops unknown keys), so it can never reach the database.
export const gradeConfigSchema = z.object({
  boulder: disciplineConfigSchema,
})

/**
 * The lead scale: every French grade, fixed for every gym. A lead route's
 * `grade` is its rank here, so rank 5 is '6a'. Points climb 10 per step with
 * 6a worth 60 (4 = 20 … 9c = 280); SQL mirrors this as `(grade + 1) * 10`.
 */
const LEAD_BAND_COLORS: Record<string, string> = {
  '4': '#22c55e', // green
  '5': '#22c55e',
  '6': '#eab308', // yellow
  '7': '#f97316', // orange
  '8': '#ef4444', // red
  '9': '#a855f7', // purple
}

/** Points for topping a lead route of this rank, before any flash bonus. */
export function leadScore(rank: number): number {
  return (rank + 1) * 10
}

export const LEAD_GRADES: GradeDef[] = FRENCH_SCALE.map((fr, i) => ({
  rank: i + 1,
  label: fr,
  color: LEAD_BAND_COLORS[fr[0]] ?? null,
  frenchMin: fr,
  frenchMax: fr,
  baseScore: leadScore(i + 1),
}))

/** Rank of 6a on the lead scale — the sensible default for a new lead route. */
export const LEAD_RANK_6A = frenchDifficulty('6a') + 1

/** True when `rank` addresses a grade that exists in this discipline's scale. */
export function isValidRank(rank: number, config: DisciplineGradeConfig): boolean {
  return Number.isInteger(rank) && rank >= 1 && rank <= config.grades.length
}

/**
 * Highest rank across both disciplines — the domain a grade chart or filter must
 * cover so no route grade is omitted, and the upper bound for route validation
 * when the discipline isn't yet known.
 */
export function maxGradeCount(config: GymGradeConfig | null | undefined): number {
  return Math.max(gradesForDiscipline(config, 'boulder').length, LEAD_GRADES.length)
}

/** How many grades a discipline's scale has — the valid rank range 1..N. */
export function gradeCountForDiscipline(
  config: GymGradeConfig | null | undefined,
  discipline: Discipline,
): number {
  return gradesForDiscipline(config, discipline).length
}

/** Look up a grade by rank within a discipline's scale. */
export function gradeByRank(rank: number, config: DisciplineGradeConfig): GradeDef | undefined {
  return config.grades.find((g) => g.rank === rank)
}

/**
 * Which grade scale a route belongs to, derived from its section's discipline.
 * `gym_sections.discipline` is 'bouldering' | 'rope' | 'other' | null; rope maps
 * to the lead scale, everything else (including an unassigned section) to
 * boulder. This is the single source of truth for the mapping — the worker
 * (route validation, standings) and the gym app both use it.
 */
export function disciplineForSection(sectionDiscipline: string | null | undefined): Discipline {
  return sectionDiscipline === 'rope' ? 'lead' : 'boulder'
}

/**
 * The ordered grade list for a discipline, easy → hard. Lead is always the
 * fixed French scale; boulder falls back to the default config so callers never
 * crash on a missing/partial config.
 */
export function gradesForDiscipline(
  config: GymGradeConfig | null | undefined,
  discipline: Discipline,
): GradeDef[] {
  if (discipline === 'lead') return LEAD_GRADES
  return (config?.boulder ?? DEFAULT_GRADE_CONFIG.boulder).grades
}

/**
 * How a route's grade rank should be shown: its configured label and colour.
 * The single source of truth for grade display across the gym and member apps.
 *
 * `discipline` picks which scale to read. Badges that know the route's section
 * pass its discipline; cross-discipline aggregates (grade charts, stats tables)
 * have only a rank and default to 'boulder' — correct for single-discipline and
 * default-config gyms, where the two scales are identical.
 *
 * Falls back gracefully: an out-of-range rank shows its number and no colour, so
 * a chart never blanks out.
 */
export function gradeDisplay(
  config: GymGradeConfig | null | undefined,
  rank: number,
  discipline: Discipline = 'boulder',
): { label: string; color: string | null } {
  const g = gradeByRank(rank, { grades: gradesForDiscipline(config, discipline) })
  return { label: g?.label ?? String(rank), color: g?.color ?? null }
}

// The legacy eight bands: labels "1".."8", the easy→hard green→red hues, and
// baseScore = rank * 100 (which reproduces the old `grade * 100` standings
// exactly). Uncalibrated (frenchMin/Max null) until an admin anchors them.
const LEGACY_BAND_COLORS = [
  '#22c55e', // 1 green
  '#10b981', // 2 emerald
  '#84cc16', // 3 lime
  '#eab308', // 4 yellow
  '#f97316', // 5 orange
  '#f59e0b', // 6 amber
  '#ef4444', // 7 red
  '#e11d48', // 8 rose
]

function legacyBands(): DisciplineGradeConfig {
  return {
    grades: LEGACY_BAND_COLORS.map((color, i) => ({
      rank: i + 1,
      label: String(i + 1),
      color,
      frenchMin: null,
      frenchMax: null,
      baseScore: (i + 1) * 100,
    })),
  }
}

/** The config every gym starts with — identical to the pre-configurable 1..8. */
export const DEFAULT_GRADE_CONFIG: GymGradeConfig = {
  boulder: legacyBands(),
}

/** The hardest grade on the V-scale preset: V0..V10. */
export const V_SCALE_MAX = 10

/**
 * The American bouldering scale, V0..V10, as a ready-made boulder config. Every
 * grade has no colour, so labels render as one neutral badge and the hold colour
 * is the only colour on a route. baseScore = rank * 100, so V0 = 100, V10 = 1100.
 */
export function vScaleBands(): DisciplineGradeConfig {
  return {
    grades: Array.from({ length: V_SCALE_MAX + 1 }, (_, v) => ({
      rank: v + 1,
      label: `V${v}`,
      color: null,
      frenchMin: null,
      frenchMax: null,
      baseScore: (v + 1) * 100,
    })),
  }
}
